import { useRef, useState } from 'react'
import { AlertCircle, ImagePlus, Loader2, Trash2 } from 'lucide-react'
import { apiClient } from '@/api/client'
import type { ApiResponse } from '@/types/api'

interface Props {
  label: string
  value: string | null
  onChange: (url: string | null) => void
  helperText: string
  previewAlt: string
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_IMAGE_SIZE = 5 * 1024 * 1024

export function BookImageUploadField({
  label,
  value,
  onChange,
  helperText,
  previewAlt,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const selectFile = () => {
    setUploadError(null)
    inputRef.current?.click()
  }

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return
    setUploadError(null)
    if (inputRef.current) inputRef.current.value = ''

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('JPG, PNG, WebP만 업로드 가능합니다.')
      return
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setUploadError('파일 크기는 5MB 이하여야 합니다.')
      return
    }

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'book-cover')
      const { data } = await apiClient.post<ApiResponse<{ url: string; filename: string }>>(
        '/admin/upload/image',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      const url = data?.data?.url
      if (url) {
        onChange(url)
      } else {
        setUploadError('업로드 응답이 비어있습니다.')
      }
    } catch (error) {
      console.error('[BookImageUploadField] image upload failed', error)
      setUploadError('업로드에 실패했습니다.')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div>
      <span className="mb-2 block text-sm font-medium text-slate-700">{label}</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(event) => handleFileSelected(event.target.files?.[0])}
      />
      {value ? (
        <div className="space-y-2">
          <div className="relative flex h-[253px] w-[180px] items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
            <img src={value} alt={previewAlt} className="size-full object-contain" />
            {isUploading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                <Loader2 className="size-6 animate-spin text-amber-500" />
              </div>
            ) : null}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectFile}
              disabled={isUploading}
              className="flex items-center gap-1 rounded bg-slate-100 px-2 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-50"
            >
              <ImagePlus className="size-3.5" /> 교체
            </button>
            <button
              type="button"
              onClick={() => { onChange(null); setUploadError(null) }}
              disabled={isUploading}
              className="flex items-center gap-1 rounded px-2 py-1.5 text-xs text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="size-3.5" /> 삭제
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={selectFile}
          disabled={isUploading}
          className="flex h-[253px] w-[180px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 transition-colors hover:border-amber-400 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-50"
        >
          {isUploading ? (
            <>
              <Loader2 className="size-6 animate-spin" />
              <span className="text-xs">업로드 중...</span>
            </>
          ) : (
            <>
              <ImagePlus className="size-6" />
              <span className="text-xs">이미지 업로드</span>
            </>
          )}
        </button>
      )}
      <p className="mt-2 max-w-[180px] text-[11px] leading-relaxed text-slate-400">{helperText}</p>
      {uploadError ? (
        <p className="mt-2 flex max-w-[180px] items-start gap-1 text-xs text-red-500">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
          <span>{uploadError}</span>
        </p>
      ) : null}
    </div>
  )
}
